/**
 * Company Employees Tab
 *
 * Manages employees for a company with add, edit, and delete functionality.
 */

import { Mail, Phone, Plus, Star, Trash2, User } from 'lucide-react';
import { useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  useCompanyEmployees,
  useCreateEmployee,
  useDeleteEmployee,
  useUpdateEmployee,
} from '../hooks';
import type { CompanyEmployee, CreateEmployeeInput } from '../types';

interface Props {
  companyId: string;
}

const INITIAL_FORM: CreateEmployeeInput = {
  name: '',
  title: '',
  role: '',
  email: '',
  phone: '',
  mobile: '',
  license_number: '',
  license_state: '',
  nmls_id: '',
  is_primary: false,
  is_active: true,
};

export default function CompanyEmployeesTab({ companyId }: Props) {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState<CompanyEmployee | null>(null);
  const [formData, setFormData] = useState<CreateEmployeeInput>(INITIAL_FORM);

  const { data: employees, isLoading } = useCompanyEmployees(companyId);
  const createEmployee = useCreateEmployee();
  const updateEmployee = useUpdateEmployee();
  const deleteEmployee = useDeleteEmployee();

  const handleOpenCreate = () => {
    setEditingEmployee(null);
    setFormData(INITIAL_FORM);
    setIsDialogOpen(true);
  };

  const handleOpenEdit = (employee: CompanyEmployee) => {
    setEditingEmployee(employee);
    setFormData({
      name: employee.name,
      title: employee.title || '',
      role: employee.role || '',
      email: employee.email || '',
      phone: employee.phone || '',
      mobile: employee.mobile || '',
      license_number: employee.license_number || '',
      license_state: employee.license_state || '',
      nmls_id: employee.nmls_id || '',
      is_primary: employee.is_primary,
      is_active: employee.is_active,
    });
    setIsDialogOpen(true);
  };

  const handleClose = () => {
    setIsDialogOpen(false);
    setEditingEmployee(null);
    setFormData(INITIAL_FORM);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      if (editingEmployee) {
        await updateEmployee.mutateAsync({
          companyId,
          employeeId: editingEmployee.id,
          data: formData,
        });
      } else {
        await createEmployee.mutateAsync({
          companyId,
          data: formData,
        });
      }
      handleClose();
    } catch (err) {
      console.error('Failed to save employee:', err);
    }
  };

  const handleDelete = async (employeeId: string) => {
    if (!confirm('Are you sure you want to delete this employee?')) return;

    try {
      await deleteEmployee.mutateAsync({ companyId, employeeId });
    } catch (err) {
      console.error('Failed to delete employee:', err);
    }
  };

  const handleInputChange = (field: keyof CreateEmployeeInput, value: string | boolean) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  if (isLoading) {
    return <div className="py-8 text-center text-gray-500">Loading employees...</div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium">Employees</h3>
        <Button onClick={handleOpenCreate} size="sm">
          <Plus className="h-4 w-4 mr-2" />
          Add Employee
        </Button>
      </div>

      {!employees?.length ? (
        <Card>
          <CardContent className="py-8 text-center">
            <User className="h-8 w-8 text-gray-400 mx-auto mb-2" />
            <p className="text-gray-500">No employees added yet</p>
            <Button variant="link" onClick={handleOpenCreate} className="mt-2">
              Add the first employee
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          {employees.map((employee) => (
            <Card key={employee.id} className={!employee.is_active ? 'opacity-60' : ''}>
              <CardContent className="pt-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{employee.name}</span>
                      {employee.is_primary && (
                        <Star className="h-4 w-4 text-yellow-500 fill-yellow-500" />
                      )}
                      {!employee.is_active && (
                        <Badge variant="secondary" className="text-xs">
                          Inactive
                        </Badge>
                      )}
                    </div>
                    {employee.title && (
                      <div className="text-sm text-gray-500">{employee.title}</div>
                    )}
                    {employee.role && (
                      <Badge variant="outline" className="mt-1 text-xs">
                        {employee.role}
                      </Badge>
                    )}

                    <div className="mt-3 space-y-1 text-sm">
                      {employee.email && (
                        <div className="flex items-center gap-2">
                          <Mail className="h-3 w-3 text-gray-400" />
                          <a
                            href={`mailto:${employee.email}`}
                            className="text-blue-600 hover:underline"
                          >
                            {employee.email}
                          </a>
                        </div>
                      )}
                      {employee.phone && (
                        <div className="flex items-center gap-2">
                          <Phone className="h-3 w-3 text-gray-400" />
                          <span>{employee.phone}</span>
                        </div>
                      )}
                      {employee.mobile && (
                        <div className="flex items-center gap-2">
                          <Phone className="h-3 w-3 text-gray-400" />
                          <span>{employee.mobile}</span>
                          <span className="text-xs text-gray-400">(Mobile)</span>
                        </div>
                      )}
                    </div>

                    {(employee.license_number || employee.nmls_id) && (
                      <div className="mt-2 text-xs text-gray-500">
                        {employee.license_number && (
                          <span>
                            License: {employee.license_number}
                            {employee.license_state && ` (${employee.license_state})`}
                          </span>
                        )}
                        {employee.license_number && employee.nmls_id && ' | '}
                        {employee.nmls_id && <span>NMLS: {employee.nmls_id}</span>}
                      </div>
                    )}
                  </div>

                  <div className="flex items-center gap-1">
                    <Button variant="ghost" size="sm" onClick={() => handleOpenEdit(employee)}>
                      Edit
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDelete(employee.id)}
                      className="text-red-600 hover:text-red-700"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingEmployee ? 'Edit Employee' : 'Add Employee'}</DialogTitle>
            <DialogDescription>
              {editingEmployee
                ? 'Update employee information'
                : 'Add a new employee to this company'}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid gap-4 md:grid-cols-2">
              <div className="md:col-span-2">
                <Label htmlFor="name">Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  required
                />
              </div>

              <div>
                <Label htmlFor="title">Title</Label>
                <Input
                  id="title"
                  value={formData.title}
                  onChange={(e) => handleInputChange('title', e.target.value)}
                  placeholder="e.g., Loan Officer"
                />
              </div>

              <div>
                <Label htmlFor="role">Role</Label>
                <Input
                  id="role"
                  value={formData.role}
                  onChange={(e) => handleInputChange('role', e.target.value)}
                  placeholder="e.g., Processor"
                />
              </div>

              <div>
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                />
              </div>

              <div>
                <Label htmlFor="phone">Phone</Label>
                <Input
                  id="phone"
                  value={formData.phone}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                />
              </div>

              <div>
                <Label htmlFor="mobile">Mobile</Label>
                <Input
                  id="mobile"
                  value={formData.mobile}
                  onChange={(e) => handleInputChange('mobile', e.target.value)}
                />
              </div>

              <div>
                <Label htmlFor="license_number">License Number</Label>
                <Input
                  id="license_number"
                  value={formData.license_number}
                  onChange={(e) => handleInputChange('license_number', e.target.value)}
                />
              </div>

              <div>
                <Label htmlFor="license_state">License State</Label>
                <Input
                  id="license_state"
                  value={formData.license_state}
                  onChange={(e) => handleInputChange('license_state', e.target.value)}
                  placeholder="e.g., CO"
                  maxLength={2}
                />
              </div>

              <div>
                <Label htmlFor="nmls_id">NMLS ID</Label>
                <Input
                  id="nmls_id"
                  value={formData.nmls_id}
                  onChange={(e) => handleInputChange('nmls_id', e.target.value)}
                />
              </div>
            </div>

            <div className="flex items-center gap-6">
              <div className="flex items-center gap-2">
                <Switch
                  id="is_primary"
                  checked={formData.is_primary}
                  onCheckedChange={(checked: boolean) => handleInputChange('is_primary', checked)}
                />
                <Label htmlFor="is_primary">Primary Contact</Label>
              </div>

              <div className="flex items-center gap-2">
                <Switch
                  id="is_active"
                  checked={formData.is_active}
                  onCheckedChange={(checked: boolean) => handleInputChange('is_active', checked)}
                />
                <Label htmlFor="is_active">Active</Label>
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleClose}>
                Cancel
              </Button>
              <Button type="submit" disabled={createEmployee.isPending || updateEmployee.isPending}>
                {editingEmployee ? 'Save Changes' : 'Add Employee'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
